import { ShieldCheck, CheckCircle, DollarSign, Home, ThumbsUp, Ruler, CalendarClock, Hammer, Users, Wrench } from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/outdoor-living.js';



export default function OutdoorLivingPage() {
  const service = {
    title: 'Outdoor Living Spaces',
    image: '/hero/best-outdoor-living-hardscaping-services-in-los-angeles.webp',
    description:
      'Custom outdoor living spaces by Modern Build. Patios, decks, fire pits, and more. Free estimate: (323) 300-4130',
    link: '/services/outdoor-living',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Outdoor Living Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Outdoor Living Spaces | Free Estimate | Modern Build</title>
        <meta name="title" content="Outdoor Living Spaces | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Custom outdoor living spaces by Modern Build. Patios, decks, fire pits, and more. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="outdoor living, patio construction, fire pit, pergola installation, backyard remodeling, outdoor design, Los Angeles, Modern Build"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/outdoor-living" />

        <meta property="og:title" content="Outdoor Living Spaces | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Custom outdoor living spaces by Modern Build. Patios, decks, fire pits, and more. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-outdoor-living-hardscaping-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/outdoor-living"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Outdoor Living Spaces | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Custom outdoor living spaces by Modern Build. Patios, decks, fire pits, and more. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-outdoor-living-hardscaping-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Outdoor Living Spaces | Free Estimate | Modern Build',
                description:
                  'Custom patios, fire pits, and outdoor living features built to last. Serving Los Angeles and surrounding areas.',
                slug: '/services/outdoor-living',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="outdoorLiving" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="OUTDOOR LIVING SERVICES"
        title="Design the Ultimate Backyard Escape"
        background="white"
        description="We build custom patios, pergolas, seating areas, and fire features designed to elevate your outdoor lifestyle in Los Angeles."
        extraText="Modern Build uses premium materials and expert craftsmanship to ensure every project is built to last and tailored to your space."
        phone="(323) 300-4130"
        image1="/split/outdoor-patio-construction-in-los-angeles.webp"
        image2="/split/outdoor-living-spaces-los-angeles-ca.webp"
      />

<Benefits
  title="What’s Included in Our Outdoor Living Services?"
  subtitle="Luxury, functionality, and durability combined"
  items={[
    {
      icon: <Hammer className="w-8 h-8 text-primary" />,
      title: "Custom Patios & Decks",
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Pergolas & Shade Structures",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Built-In Seating & Retaining Walls",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Outdoor Kitchens & BBQs",
       href: "/services/kitchen-remodeling",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Fire Pits & Entertainment Zones",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "Lighting & Utility Integration",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Flexible Financing Available",
      href: "/financing",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Work Performed by Licensed, Bonded & Insured Contractor",
      href: "/about-us",
    },
  ]}
/>


      <ContentBlock
        subtitle="LOS ANGELES OUTDOOR EXPERTS"
        title="Turn Your Backyard Into an Oasis"
        description="Modern Build designs and builds personalized outdoor living spaces that combine function and beauty. Whether it’s a cozy patio or a fully equipped outdoor kitchen, we help bring your vision to life."
        extraText="From planning to final walkthrough, we manage the entire process so you can relax and enjoy your new space."
        buttonText="Request a Free Outdoor Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Outdoor Living Build Process"
        subtitle="Crafted with care from start to finish"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "On-Site Consultation",
            description: "We assess your yard, explore ideas, and create a plan for your outdoor goals.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design & Layout Planning",
            description: "We develop custom plans with material options that suit your home’s style and space.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Construction & Installation",
            description: "Our team builds your outdoor features with precision, care, and quality craftsmanship.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Walkthrough & Completion",
            description: "We review the finished space with you and ensure every detail exceeds expectations.",
          },
        ]}
      />

      <Split
        subtitle="BUILT TO ENJOY"
        title="Elevate Your Outdoor Lifestyle"
        background="white"
        description="Whether you're entertaining guests or relaxing in peace, a well-designed outdoor space adds comfort, beauty, and value to your home."
        extraText="Modern Build delivers outdoor spaces that impress today and endure tomorrow."
        phone="(323) 300-4130"
        image1="/split/luxury-backyard-los-angeles.webp"
        image2="/split/outdoor-fire-pit-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Let’s Bring Your Backyard Vision to Life"
        subtitle="Start with a Free Design Consultation"
        buttonText="Book Your Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="outdoor-living" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
